<template>
    <div class="lms__superadmin_workspaces_edit">
        <div class="lms__superadmin_workspaces_edit_header">
            <view-actions
                ref="viewactions"
                :breadcrumb-active="true"
                :breadcrumb-items="[
                    {
                        name: 'Workspaces',
                        to: { name: 'lms-superadmin-workspace-list' }
                    }
                ]"
                :cancel-button-text="user.isSuperadmin() || user.isMasterAdministrator() ? 'Discard' : 'Go back'"
                :cancel-notify-active="true"
                :cancel-notify-content-text="'This action will remove all changes. Continue?'"
                :cancel-notify-submit-text="'Discard'"
                :custom-button-active="true"
                :custom-button-text="'Restore defaults'"
                :notify-title="'workspace'"
                :notify-process="'stop updating'"
                :submit-button-active="user.isSuperadmin() || user.isMasterAdministrator() ? true : false"
                :submit-button-text="'Update'"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-event-name="'workspace-update'"
                :trash-button-active="user.isSuperadmin() || user.isMasterAdministrator()"
                :trash-notify-content="'Archiving this workspace means users will lose access to the workspace and features.'"
                :trash-notify-prevent-close="true"
                :title="`Editing: ${workspaceName}`"
                @emit-notify-cancel-submit="workspaceStopEdit"
                @emit-submit="performSubmitValidate"
                @emit-custom="openRestoreDefaultNotify"
                @emit-notify-remove-submit="workspaceRemove"
            />
        </div>
        <div class="lms__superadmin_workspaces_edit_main">
            <div class="container fullheight">
                <div class="columns is-block-mobile fullheight m-t-b-0">
                    <div class="column is-narrow p-t-b-0">
                        <component-sidebar :nav-items="sidebarNav"></component-sidebar>
                    </div>
                    <div class="column custom--change p-t-b-0">
                        <section class="lms__superadmin_workspaces_edit_main_content">
                            <keep-alive>
                                <transition name="fade" mode="out-in">
                                    <router-view/>
                                </transition>
                            </keep-alive>
                        </section>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="poprestoredefaults"
            :cancel-button-text="'Cancel'"
            :class="'pop__notify--release'"
            :content-text="'Are you sure you want to restore all customized settings to default?'"
            :prevent-close="true"
            :size="'medium'"
            :submit-button-text="'Restore defaults'"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'course-restore-defaults'"
            :title-text="'Restore defaults'"
            @emit-submit="performRestoreDefaults"
        />
    </div>
</template>

<script>
import ViewActions from '@/components/default/shared/ViewActions'
import PopNotify from '@/components/default/shared/PopNotify'
import { validateBeforeRouteLeave, performSubmitValidate } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentSidebar from '@/components/default/shared/ComponentSidebar'

export default {
  name: 'lms-superadmin-workspace-edit',
  components: { ComponentSidebar, PopNotify, ViewActions },
  mixins: [validateBeforeRouteLeave, performSubmitValidate],
  data () {
    return {
      user: new User()
    }
  },
  computed: {
    workspaceName () {
      return this.$store.getters.getLmsSuperadminWorkspaceName
    },
    workspaceId () {
      return this.$store.getters.getLmsSuperadminWorkspaceId
    },
    customerHasAPIIntegrationService () {
      return Boolean(this.$store.getters.getLmsSuperadminCustomerSubscriptionsByKey('API').length)
    },
    //
    rootItemName () {
      return 'workspace'
    },
    nameActionCreate () {
      return 'lmsSuperadminWorkspaceCreate'
    },
    nameActionUpdate () {
      return 'lmsSuperadminWorkspaceUpdate'
    },
    nameActionReset () {
      return 'lmsSuperadminWorkspaceReset'
    },
    sidebarNav () {
      let navArr = []

      navArr.push({ label: 'Details', name: 'lms-superadmin-workspace-edit-details' })
      navArr.push({ label: 'Appearance', name: 'lms-superadmin-workspace-edit-appearance' })
      navArr.push({ label: 'Onboarding text', name: 'lms-superadmin-workspace-edit-onboarding' })
      navArr.push({ label: 'Footer', name: 'lms-superadmin-workspace-edit-footer' })
      navArr.push({ label: 'User fields', name: 'lms-superadmin-workspace-edit-custom-fields' })
      if (this.user.isMasterAdministrator()) {
        navArr.push({ label: 'Sign-up and authentication', name: 'lms-superadmin-workspace-edit-self-registration' })
        navArr.push({ label: 'Feedback forms', name: 'lms-superadmin-workspace-edit-feedback-forms' })
      }
      navArr.push({ label: 'Certificates', name: 'lms-superadmin-workspace-edit-certificates' })
      if (this.customerHasAPIIntegrationService) {
        navArr.push({ label: 'API integration', name: 'lms-superadmin-workspace-edit-api-integration' })
      }

      return navArr
    }
  },
  methods: {
    performRestoreDefaults () {
      this.$store.dispatch('lmsSuperadminWorkspaceRestoreDefaults').finally(() => {
        if (this.$refs.poprestoredefaults) {
          this.$refs.poprestoredefaults.closeNotify()
        }
      })
    },
    openRestoreDefaultNotify () {
      this.$refs.poprestoredefaults.openNotify()
    },
    performSubmitValidateValidationCallback () {
      this.$eventBus.$emit('stop-workspace-update')
    },
    performSubmitValidateFinallyCallback () {
      this.$eventBus.$emit('stop-workspace-update')
    },
    allowRedirectBeforeRouteLeave () {
      this.$eventBus.$emit('workspace-submit')
    },
    //
    workspaceStopEdit () {
      this.allowRedirectBeforeRouteLeave()
      const routeQuery = this.$route.query
      if (routeQuery.customerId) {
        this.$router.push({ name: 'lms-superadmin-customer-edit-workspaces', params: { id: routeQuery.customerId } })
      } else {
        this.$router.push({ name: 'lms-superadmin-workspace-list' })
      }
    },
    workspaceRemove () {
      this.allowRedirectBeforeRouteLeave()
      this.$store.dispatch('lmsSuperadminWorkspaceRemove', this.workspaceId).catch(() => {
        this.$refs.viewactions.$refs.popnotifyremove.closeNotify()
      })
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddedit.scss";
</style>
